import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export const Route = createFileRoute('/client/processes/$processId/')({
  component: ClientProcessInfoPage,
});

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function formatDate(date: Date | string | null | undefined) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="p-4 rounded-[12px] bg-gray-50 border border-gray-100">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="text-sm text-[#06141B] mt-1 whitespace-pre-wrap">{value || '-'}</p>
    </div>
  );
}

function ClientProcessInfoPage() {
  const { processId } = Route.useParams();

  const { data: process, isLoading, error } = useQuery<any>({
    queryKey: ['client-portal', 'process', processId],
    queryFn: async () => {
      const r = await api.get(`/client-portal/processes/${processId}`);
      return r.data?.data ?? r.data;
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <p className="text-sm text-gray-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Carregando processo...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <p className="text-sm text-red-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
          Erro ao carregar o processo.
        </p>
      </div>
    );
  }

  if (!process) return null;

  const stats = process.clientStats;

  return (
    <div className="space-y-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className="bg-white rounded-[15px] p-5 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-[#06141B] truncate">
              {process.internalTitle || process.parties || 'Processo'}
            </h2>
            <p className="text-[12px] text-gray-600 mt-1">{process.processNumber}</p>
            {process.parties && process.internalTitle ? (
              <p className="text-[12px] text-gray-500 mt-1 whitespace-pre-wrap">{process.parties}</p>
            ) : null}
          </div>
          <span className="text-xs px-2.5 py-1 rounded-[10px] bg-gray-100 text-gray-700 whitespace-nowrap">
            {process.status}
          </span>
        </div>

        {stats ? (
          <div className="mt-5 p-4 rounded-[12px] bg-[#F9FAFB] border border-gray-100">
            <p className="text-[12px] font-semibold text-[#06141B] mb-3">Resumo</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
              <div>
                <p className="text-gray-500">Tarefas (total)</p>
                <p className="font-semibold text-[#06141B]">{stats.totalTasks ?? 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Em aberto</p>
                <p className="font-semibold text-[#06141B]">{stats.openTasks ?? 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Concluídas</p>
                <p className="font-semibold text-[#06141B]">{stats.doneTasks ?? 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Canceladas</p>
                <p className="font-semibold text-[#06141B]">{stats.canceledTasks ?? 0}</p>
              </div>
              <div className="col-span-2 md:col-span-4">
                <p className="text-gray-500">Tempo de execução (tarefas)</p>
                <p className="font-semibold text-[#06141B]">
                  {stats.totalExecutionHours != null
                    ? `${stats.totalExecutionHours} h (${Math.round(stats.totalExecutionMinutes ?? 0)} min)`
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {process.tags?.length ? (
          <div className="mt-5">
            <p className="text-[12px] font-semibold text-gray-700 mb-2">Tags</p>
            <div className="flex flex-wrap gap-2">
              {process.tags.map((tag: any) => (
                <span
                  key={tag.id}
                  className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-800"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Info label="Tribunal" value={process.court} />
          <Info label="Classe" value={process.judicialClass} />
          <Info label="Órgão" value={process.courtOrg} />
          <Info label="Vara" value={process.courtRoom} />
          <Info label="Fase" value={process.phase} />
          <Info label="Substatus" value={process.substatus} />
          <Info label="Prioridade" value={process.priority} />
          <Info label="Número CNJ" value={process.cnjNumber} />
          <Info label="Jurisdição" value={process.jurisdiction} />
          <Info label="Polo do cliente" value={process.clientPole} />
          <Info label="Justiça secreta" value={process.secretJustice ? 'Sim' : 'Não'} />
          <Info label="Valor da causa" value={formatCurrency(process.caseValue)} />
          <Info label="Valor da causa atualizado" value={formatCurrency(process.updatedCaseValue)} />
          <Info label="Valor da condenação" value={formatCurrency(process.condemnationValue)} />
          <Info label="Proveito econômico" value={formatCurrency(process.economicBenefitValue)} />
          <Info label="Distribuição" value={formatDate(process.distributionDate)} />
          <Info label="Autuação / protocolo" value={formatDate(process.filingDate)} />
          <Info label="Citação" value={formatDate(process.citationDate)} />
          <Info label="Cadastrado em" value={formatDate(process.createdAt)} />
          <Info label="Última atualização" value={formatDate(process.updatedAt)} />
        </div>

        {process.responsibleMember?.user?.name ? (
          <div className="mt-5">
            <Info label="Responsável" value={process.responsibleMember.user.name} />
          </div>
        ) : null}

        {process.summary && (
          <div className="mt-5">
            <p className="text-[12px] font-semibold text-gray-700">Resumo</p>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{process.summary}</p>
          </div>
        )}

        {process.actionObject && (
          <div className="mt-5">
            <p className="text-[12px] font-semibold text-gray-700">Objeto da ação</p>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{process.actionObject}</p>
          </div>
        )}

        {process.parts?.length ? (
          <div className="mt-5">
            <p className="text-[12px] font-semibold text-gray-700 mb-2">Partes</p>
            <ul className="space-y-2">
              {process.parts.map((part: any) => (
                <li
                  key={part.id}
                  className="text-sm p-3 rounded-[12px] bg-gray-50 border border-gray-100"
                >
                  <span className="font-medium text-[#06141B]">{part.name}</span>
                  {part.pole ? (
                    <span className="text-xs text-gray-600 ml-2">({part.pole})</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {process.archivedAt && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-[14px] font-semibold text-[#06141B] mb-3">Arquivamento</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Info label="Data de arquivamento" value={formatDate(process.archivedAt)} />
              <Info label="Arquivado por" value={process.archivedBy?.name} />
              <Info label="Tipo de conclusão" value={process.conclusionType} />
            </div>
            {process.conclusionNotes ? (
              <div className="mt-3">
                <p className="text-[12px] font-semibold text-gray-700">Observações</p>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                  {process.conclusionNotes}
                </p>
              </div>
            ) : null}
          </div>
        )}

        {process.linkedProcesses?.length ? (
          <div className="mt-5">
            <p className="text-[12px] font-semibold text-gray-700 mb-2">Processos vinculados</p>
            <ul className="text-sm text-gray-700 space-y-1">
              {process.linkedProcesses.map((lp: any) => (
                <li key={lp.id}>
                  {lp.processNumber}
                  {lp.internalTitle ? ` — ${lp.internalTitle}` : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
